import { UserAvatar } from "@/components/molecules/user-avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { HistoryActor } from "@/features/types";
import { useDateFormat } from "@/features/utils/i18n";
import { GearSixIcon } from "@phosphor-icons/react";
import type { ReactNode } from "react";

/** One version of an audited row that changed something a reader can see. */
export interface AuditHistoryItem {
  at: number;
  actor: HistoryActor;
  /** What happened: the fields that moved, or "Created". */
  label: string;
  /**
   * The new value of what moved — and **only** of that. A line that also repeats
   * the fields it left untouched reads as if they had changed too.
   */
  values?: ReactNode[];
}

/**
 * The audit trail of a row, folded: it answers "who changed this", rarely asked.
 * Each line says when, who (a person by their face, anything else — the call, the
 * queue, the wrap-up — as the system), what moved and what it became.
 *
 * The component owns the layout and nothing else: the server decides which versions
 * count as a change, and each page words them (`label`, `values`) in its own terms,
 * so any audited entity can use it.
 */
export function AuditHistory({
  items,
  summary,
  systemLabel,
}: {
  items: AuditHistoryItem[];
  /** The folded line, e.g. "History (3 changes)". */
  summary: string;
  systemLabel: string;
}) {
  const { format } = useDateFormat();
  if (items.length === 0) return null;

  return (
    <details className="group text-sm">
      <summary className="cursor-pointer text-muted-foreground select-none hover:text-foreground">
        {summary}
      </summary>
      <ol className="mt-3 space-y-2 text-xs">
        {items.map((item, index) => (
          <li
            key={index}
            className="flex flex-wrap items-center gap-x-2 gap-y-1 text-muted-foreground"
          >
            <ActorMark actor={item.actor} systemLabel={systemLabel} />
            <span className="tabular-nums">{format(item.at, "PPp")}</span>
            <span className="font-medium text-foreground">{item.label}</span>
            {(item.values || []).map((value, i) => (
              <span key={i} className="tabular-nums">
                {value}
              </span>
            ))}
          </li>
        ))}
      </ol>
    </details>
  );
}

export function ActorMark({
  actor,
  systemLabel,
}: {
  actor: HistoryActor | null | undefined;
  systemLabel: string;
}) {
  if (!actor || actor.kind === "system") {
    return (
      <span className="flex items-center gap-1">
        <GearSixIcon className="size-3.5" />
        {systemLabel}
      </span>
    );
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="flex cursor-default items-center">
          <UserAvatar avatar={actor.avatar} name={actor.name} size="sm" />
        </span>
      </TooltipTrigger>
      <TooltipContent>{actor.name}</TooltipContent>
    </Tooltip>
  );
}
